/**
 * Shared description of a thrown value for Application Insights (GlobalErrorHandler and the window error
 * listeners in MonitoringService).
 *
 * Anything thrown that is not an Error used to go through String(error), and almost everything the app throws is
 * a plain object (an HttpErrorResponse, an MSAL error, the rejected body BaseService.handleError throws), so it
 * arrived as the literal text "[object Object]". URLs are reduced to their path: query strings can carry search
 * text, names and emails, and never leave the browser (bug 40429).
 */
export function describeError(error: unknown): string {
  if (error === null || error === undefined) return 'Unknown error (null/undefined thrown)';
  if (typeof error === 'string') return error;

  if (typeof error === 'object') {
    const e = error as Record<string, unknown>;

    // HttpErrorResponse and anything else carrying HTTP context: method, status and path identify it.
    if (typeof e['status'] === 'number' || e['url']) {
      const status = e['status'] ?? '';
      const network = status === 0 ? 'network failure (request blocked or dropped)' : (e['statusText'] ?? '');
      const method = e['method'] ?? '';
      const url = typeof e['url'] === 'string' ? urlPath(e['url'] as string) : '';
      const inner = (e['error'] && typeof e['error'] === 'object')
        ? (((e['error'] as Record<string, unknown>)['title'] ?? (e['error'] as Record<string, unknown>)['detail']) ?? '')
        : (e['error'] ?? '');
      const operation = e['operation'] ? `(${e['operation']})` : '';
      // HttpErrorResponse.message repeats the full URL, so query strings are cut out of the whole text.
      return `HTTP ${status} ${network} ${method} ${url} ${operation} ${e['message'] ?? ''} ${inner}`
        .replace(/\?[^\s:]*/g, '').replace(/\s+/g, ' ').trim();
    }

    // MSAL errors and most library errors carry errorCode/message.
    if (e['errorCode'] || e['message']) {
      return `${e['name'] ?? e['errorCode'] ?? 'Error'}: ${e['message'] ?? e['errorMessage'] ?? ''}`.trim();
    }

    try {
      const json = JSON.stringify(error);
      if (json && json !== '{}') return json.length > 500 ? json.slice(0, 500) + '…' : json;
    } catch {
      // Circular or otherwise unserialisable: fall through to the constructor name.
    }

    return `Unserialisable ${Object.prototype.toString.call(error)}`;
  }

  return String(error);
}

/** A URL without its query string or fragment. */
export function urlPath(url: string): string {
  if (!url) return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.substring(0, cut);
}

/**
 * True for browser notices that are not faults and must not be reported as exceptions (bug 40425): the
 * ResizeObserver loop notice is raised by the browser when a layout change and an observer meet in one frame.
 */
export function isBenignError(message: string | null | undefined): boolean {
  return !!message && /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i.test(message);
}
